import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, ShieldCheck, Lock } from "lucide-react";
import { Overline } from "./Layout";
import { requestCode, verifyRegistration, resumeRegistration, clearSession } from "../lib/register";

/**
 * Email-ownership verification gate (magic code) inserted BEFORE the existing
 * registration/booking form. On success calls onVerified({ token, next, prefill,
 * email }); the caller then renders its existing form, prefilled + carrying the
 * token. Renders its own terminal states for members-only and already-registered.
 */
export default function VerifyGate({ targetId, membershipCtaUrl = "/", onVerified, onCancel }) {
  const [step, setStep] = useState("email"); // email | code | members_only | already
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null); // already-registered status
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true); // trying to resume an existing session

  // On mount, reuse a still-valid verified session so the visitor skips the code
  // step for repeat registrations. Falls back to the email step if none/expired.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await resumeRegistration(targetId);
      if (cancelled) return;
      if (d?.next === "members_only") { setStep("members_only"); setChecking(false); return; }
      if (d?.next === "already_registered") { setStatus(d.status); setStep("already"); setChecking(false); return; }
      if (d?.registrationToken) {
        onVerified({ token: d.registrationToken, next: d.next, prefill: d.prefill, email: d.email });
        return; // hands off to the form; this component unmounts
      }
      setChecking(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOver = () => { clearSession(); setStatus(null); setCode(""); setStep("email"); setChecking(false); };

  const send = async () => {
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await requestCode(em);
      toast.success("Code sent — check your inbox");
      setStep("code");
    } catch (e) {
      if (e.retryAfter) toast.error(`Too many requests — try again in ${e.retryAfter}s`);
      else toast.error("We couldn't send a code, try again shortly");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const d = await verifyRegistration(targetId, email.trim().toLowerCase(), code.trim());
      if (d.next === "members_only") return setStep("members_only");
      if (d.next === "already_registered") { setStatus(d.status); return setStep("already"); }
      onVerified({ token: d.registrationToken, next: d.next, prefill: d.prefill, email: d.email || email.trim().toLowerCase() });
    } catch (e) {
      const s = e.response?.status;
      if (s === 400) toast.error("Wrong or expired code — request a new one");
      else if (s === 503) toast.error("We couldn't confirm right now — please try again");
      else toast.error("Verification failed");
    } finally { setBusy(false); }
  };

  if (step === "members_only") {
    return (
      <div className="mt-2 text-center" data-testid="verify-members-only">
        <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center" style={{ background: "hsl(var(--vd-purple) / 0.24)", color: "hsl(var(--vd-purple-ink))" }}><Lock size={18} /></div>
        <div className="mt-3 font-heading text-xl">This event is for members</div>
        <div className="text-sm text-muted-foreground mt-1">Venture Dock members get access to private events, the space, and the community.</div>
        <a href={membershipCtaUrl} className="btn-primary w-full mt-5 justify-center" data-testid="verify-join-cta">Learn about membership <ArrowRight size={14} /></a>
        <button onClick={startOver} className="btn-ghost w-full text-xs mt-2" data-testid="verify-switch-email">Use a different email</button>
      </div>
    );
  }

  if (step === "already") {
    return (
      <div className="mt-2 text-center" data-testid="verify-already-registered">
        <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center" style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}><ShieldCheck size={18} /></div>
        <div className="mt-3 font-heading text-xl">You're already registered</div>
        <div className="text-sm text-muted-foreground mt-1">Current status: <span className="font-mono">{status || "confirmed"}</span></div>
      </div>
    );
  }

  if (checking) {
    return <div className="mt-2 text-sm text-muted-foreground" data-testid="verify-checking">Checking…</div>;
  }

  return (
    <div className="mt-2" data-testid="verify-gate">
      <div className="flex items-center justify-between">
        <Overline>{step === "email" ? "Verify your email" : "Enter your code"}</Overline>
        <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground" data-testid="verify-cancel">Cancel</button>
      </div>

      {step === "email" ? (
        <div className="mt-3 space-y-3">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5"><Mail size={11} /> Email</div>
            <input type="email" className="input-field" placeholder="you@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="verify-email" autoFocus />
          </div>
          <div className="text-xs text-muted-foreground">We'll email you a 6-digit code to confirm this address.</div>
          <button onClick={send} disabled={busy} className="btn-primary w-full" data-testid="verify-send-code">
            {busy ? "Sending…" : "Send code"} <ArrowRight size={14} />
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-muted-foreground">Code sent to <span className="font-mono">{email}</span>. Expires in 15 minutes.</div>
          <input inputMode="numeric" maxLength={6} className="input-field text-center tracking-[0.5em] font-mono text-lg" placeholder="••••••"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verify()} data-testid="verify-code" autoFocus />
          <button onClick={verify} disabled={busy} className="btn-primary w-full" data-testid="verify-submit">
            {busy ? "Verifying…" : "Verify"} <ArrowRight size={14} />
          </button>
          <button onClick={send} disabled={busy} className="btn-ghost w-full text-xs" data-testid="verify-resend">Resend code</button>
        </div>
      )}
    </div>
  );
}
